/**
 * Template Review — every call the review screen makes to the admin API.
 *
 * Kept in one module so the page components hold no URLs and no auth plumbing,
 * and so the request shapes are testable without rendering anything.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API_URL}/api/admin/content/templates`;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const request = async (url, options = {}) => {
  const res = await fetch(url, {
    ...options,
    headers: { ...(options.body ? { 'Content-Type': 'application/json' } : {}), ...authHeaders(), ...(options.headers || {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = typeof body.detail === 'string' ? body.detail : body.detail?.message;
    throw new Error(detail || `HTTP ${res.status}`);
  }
  return res.json();
};

/**
 * Filters → query string. `source: 'all'` sends no source at all, because the
 * list endpoint treats an absent source as "everything".
 */
export const filterParams = (f) => {
  const params = new URLSearchParams();
  if (f.status) params.set('status', f.status);
  if (f.source && f.source !== 'all') params.set('source', f.source);
  if (f.category) params.set('category', f.category);
  if (f.style) params.set('style', f.style);
  if (f.format) params.set('template_format', f.format);
  if (f.search && f.search.trim()) params.set('search', f.search.trim());
  return params;
};

export const listTemplates = (filters, { page, limit, sort }) => {
  const params = filterParams(filters);
  params.set('page', String(page));
  params.set('limit', String(limit));
  params.set('sort', sort);
  return request(`${BASE}?${params}`);
};

export const countTemplates = (filters) => request(`${BASE}/count?${filterParams(filters)}`).then((r) => r.count);

export const reviewSummary = (source) => {
  const params = new URLSearchParams();
  if (source && source !== 'all') params.set('source', source);
  return request(`${BASE}/review-summary?${params}`);
};

/** approve | publish | unpublish | submit | reject | request-revision */
export const workflow = (id, action, body = {}) =>
  request(`${BASE}/${id}/${action}`, { method: 'PATCH', body: JSON.stringify(body) });

export const bulkAction = (ids, action, extra = {}) =>
  request(`${BASE}/bulk-action`, { method: 'POST', body: JSON.stringify({ ids, action, ...extra }) });

export const removeTemplate = (id) => request(`${BASE}/${id}`, { method: 'DELETE' });

export const duplicateTemplate = (id) => request(`${BASE}/${id}/duplicate`, { method: 'POST' });

export const updateMetadata = (id, fields) => request(`${BASE}/${id}`, { method: 'PUT', body: JSON.stringify(fields) });

export const similarTemplates = (id) => request(`${BASE}/${id}/similar`);
